import React from 'react';

import { HttpsLink } from '@/sections/poe/components/HttpsLink';
import { isHttpsUrl } from '@/sections/poe/utils/httpsUrl';
import { cn } from '@/utils/classname';

export interface PoeContentProps {
  content: string | string[] | React.ReactNode;
}

/** Whitespace-delimited https:// candidates; capture group preserves all text. */
const HTTPS_TOKEN_REGEX = /(https:\/\/[^\s]+)/g;

/**
 * Splits text and renders embedded https URL tokens as safe anchors while
 * keeping every other character (including whitespace/newlines) as plain
 * text, so <pre> formatting is preserved exactly. Only tokens passing the
 * strict `isHttpsUrl` guard become anchors; everything else stays inert.
 */
const linkifyHttpsUrls = (text: string): React.ReactNode[] =>
  text
    .split(HTTPS_TOKEN_REGEX)
    .map((part, index) =>
      isHttpsUrl(part) ? <HttpsLink key={index} value={part} /> : part
    );

export const PoeContent = ({ content }: PoeContentProps) => {
  if (typeof content === 'string' && isHttpsUrl(content)) {
    return (
      <div
        tabIndex={-1}
        className={cn(
          'text-chariot-text-primary max-w-none overflow-auto',
          'text-wrap focus:outline-hidden'
        )}
      >
        <HttpsLink value={content} />
      </div>
    );
  }
  if (typeof content === 'string' || Array.isArray(content)) {
    return (
      <pre
        className={cn(
          'prose prose-sm text-chariot-text-primary max-w-none overflow-auto',
          'text-wrap focus:outline-hidden'
        )}
        tabIndex={-1}
      >
        {linkifyHttpsUrls(
          Array.isArray(content) ? content.join('\n') : String(content)
        )}
      </pre>
    );
  }
  return (
    <div
      tabIndex={-1}
      className={cn(
        'text-chariot-text-primary max-w-none overflow-auto',
        'text-wrap focus:outline-hidden'
      )}
    >
      {content}
    </div>
  );
};
